import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { AuthUser } from '@/lib/auth/types'
import { queryClient } from '@/lib/api/queryClient'
import { useWorkspaceStore } from '@/stores/workspace'

const STORAGE_KEY = 'moose.auth'

interface PersistedSession {
  token: string
  user: AuthUser
}

function readSession(): PersistedSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<PersistedSession>
    // Sessions saved before the user had firstName/lastName/hasPassword are discarded (re-login).
    return parsed.token &&
      parsed.user &&
      typeof parsed.user.firstName === 'string' &&
      typeof parsed.user.hasPassword === 'boolean'
      ? { token: parsed.token, user: parsed.user }
      : null
  } catch {
    return null
  }
}

function writeSession(session: PersistedSession | null): void {
  try {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage unavailable (private mode, blocked): session lasts until reload.
  }
}

export const useAuthStore = defineStore('auth', () => {
  const stored = readSession()

  const token = ref<string | null>(stored?.token ?? null)
  const user = ref<AuthUser | null>(stored?.user ?? null)

  const isAuthenticated = computed(() => token.value !== null)

  function persist(): void {
    writeSession(token.value && user.value ? { token: token.value, user: user.value } : null)
  }

  function login(newToken: string, newUser: AuthUser): void {
    token.value = newToken
    user.value = newUser
    persist()
  }

  /** Updates the signed-in user's profile data and keeps the persisted copy in sync. */
  function setUser(newUser: AuthUser): void {
    user.value = newUser
    persist()
  }

  function logout(): void {
    token.value = null
    user.value = null
    persist()
    queryClient.clear()
    useWorkspaceStore().reset()
  }

  return { token, user, isAuthenticated, login, setUser, logout }
})
